import type { Run } from '@noconflict/contracts';
import type * as React from 'react';
import { cn } from '@/shared/lib/cn';

export type StatusTone = 'neutral' | 'info' | 'progress' | 'success' | 'warning' | 'danger';

const toneClasses: Record<StatusTone, { badge: string; dot: string }> = {
  neutral: { badge: 'bg-status-neutral-bg text-status-neutral', dot: 'bg-status-neutral' },
  info: { badge: 'bg-status-info-bg text-status-info', dot: 'bg-status-info' },
  progress: { badge: 'bg-status-progress-bg text-status-progress', dot: 'bg-status-progress' },
  success: { badge: 'bg-status-success-bg text-status-success', dot: 'bg-status-success' },
  warning: { badge: 'bg-status-warning-bg text-status-warning', dot: 'bg-status-warning' },
  danger: { badge: 'bg-status-danger-bg text-status-danger', dot: 'bg-status-danger' },
};

/** Run states → tones (Q30): queued is neutral, running pulses, failed and timeout are danger. */
export function runStatusToTone(status: Run['status']): StatusTone {
  const tones: Record<Run['status'], StatusTone> = {
    queued: 'neutral',
    running: 'progress',
    complete: 'success',
    failed: 'danger',
    timeout: 'danger',
  };
  return tones[status];
}

type StatusBadgeProps = Omit<React.ComponentProps<'span'>, 'children'> & {
  tone: StatusTone;
  size?: 'default' | 'compact';
  /** The text label. Required: colour never carries the state alone. */
  children: React.ReactNode;
};

/** Dot + tinted pill + text label for a state. The dot pulses while `progress`. */
function StatusBadge({ tone, size = 'default', className, children, ...props }: StatusBadgeProps) {
  return (
    <span
      data-slot="status-badge"
      data-tone={tone}
      data-size={size}
      className={cn(
        'inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full text-xs font-bold whitespace-nowrap',
        size === 'compact' ? 'px-2 py-0' : 'px-2.5 py-0.5',
        toneClasses[tone].badge,
        className,
      )}
      {...props}
    >
      <span
        data-slot="status-dot"
        aria-hidden="true"
        className={cn(
          'size-2 rounded-full',
          toneClasses[tone].dot,
          tone === 'progress' && 'motion-safe:animate-pulse',
        )}
      />
      <span>{children}</span>
    </span>
  );
}

export { StatusBadge, type StatusBadgeProps };
